import { Component, computed, effect, inject, OnInit, viewChild } from '@angular/core';
import { OrganizationSidebarLayout } from '../../../organizations/components/organization-sidebar-layout/organization-sidebar-layout';
import { PageHeader } from '../../../layout/components/page-header/page-header';
import { PageTitle } from '../../../layout/components/page-title/page-title';
import { Button, ConfirmService, NotificationService, SimpleDialog } from 'excellence-ui';
import { NgIcon } from '@ng-icons/core';
import { DocumentsTable } from '../../components/documents-table/documents-table';
import { DocumentsFilter, DocumentsStore, getInitialFilter } from '../../stores/documents.store';
import { OrganizationsStore } from '../../../organizations/store/organizations.store';
import { InfiniteScrollDirective } from '../../../../shared/directives/infinite-scroll';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentsFilterForm } from '../../components/documents-filter-form/documents-filter-form';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DocumentUploadDialog } from '../../dialogs/document-upload-dialog/document-upload-dialog';
import { DocumentsDeletedNotification } from '../../notifications/documents-deleted-notification/documents-deleted-notification';
import { TutorialDocumentsPopover } from '../../../tutorials/components/tutorial-documents-popover/tutorial-documents-popover';
import { VideoTutorialService } from '../../../tutorials/services/video-tutorial.service';
import { DocumentsEmptyBox } from '../../components/documents-empty-box/documents-empty-box';
import { DocumentsFilterNoResults } from '../../components/documents-filter-no-results/documents-filter-no-results';
import { HttpErrorResponse } from '@angular/common/http';
import { DisableInDemoModeDirective } from '../../../demo/directives/disable-in-demo-mode';

@Component({
  selector: 'app-documents-page',
  imports: [
    OrganizationSidebarLayout,
    PageHeader,
    PageTitle,
    NgIcon,
    Button,
    DocumentsTable,
    InfiniteScrollDirective,
    DocumentsFilterForm,
    TranslocoPipe,
    SimpleDialog,
    DocumentUploadDialog,
    TutorialDocumentsPopover,
    DocumentsEmptyBox,
    DocumentsFilterNoResults,
    DisableInDemoModeDirective,
  ],
  templateUrl: './documents-page.html',
})
export class DocumentsPage implements OnInit {
  private videoTutorialService = inject(VideoTutorialService);
  private documentsStore = inject(DocumentsStore);
  private organizationsStore = inject(OrganizationsStore);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  private confirmService = inject(ConfirmService);

  private table = viewChild<DocumentsTable>('table');
  protected selectedRows = computed(() => {
    const selectedRows = this.table()?.selectedRows() ?? {};
    return Object.keys(selectedRows).filter((id) => selectedRows[id]) ?? [];
  });

  protected documents = this.documentsStore.pages;
  protected total = this.documentsStore.total;
  protected isInitialFilterActive = this.documentsStore.isInitialFilterActive;
  protected organizationId = this.organizationsStore.selectedId;

  private tutorialPopover = viewChild(TutorialDocumentsPopover);

  protected showLoadMorePlaceholders = computed(
    () => !this.documentsStore.isLoading() && !this.documentsStore.areAllPagesPresent(),
  );

  protected orderBy = computed(() => this.documentsStore.filter().data.orderBy ?? null);
  protected orderDirection = computed(
    () => this.documentsStore.filter().data.orderDirection ?? null,
  );
  protected limit = this.documentsStore.limit;

  constructor() {
    let previousFilter: DocumentsFilter | null = null;
    effect(() => {
      const filter = this.documentsStore.filter();
      if (JSON.stringify(filter.data) !== JSON.stringify(previousFilter?.data)) {
        this.documentsStore.onChangeFilter();
      }
      previousFilter = filter;
    });
    effect(() => {
      const popover = this.tutorialPopover();
      if (popover && this.videoTutorialService.value()?.LumoTutorialDocuments === false) {
        popover.showPopover();
        this.videoTutorialService.markTutorialAsViewed('LumoTutorialDocuments');
      }
    });
  }

  ngOnInit(): void {
    this.initFilter();
  }

  private initFilter() {
    const fileType = this.activatedRoute.snapshot.queryParamMap.get('fileType');
    const mimeType = this.activatedRoute.snapshot.queryParamMap.get('mimeType');
    const filename = this.activatedRoute.snapshot.queryParamMap.get('filename');
    const orderBy = this.activatedRoute.snapshot.queryParamMap.get('orderBy');
    const orderDirection = this.activatedRoute.snapshot.queryParamMap.get('orderDirection');

    const filterUpdate: DocumentsFilter = getInitialFilter();

    if (
      fileType === 'SUPPLIER_CONTRACT' ||
      fileType === 'SUPPLIER_INVOICE' ||
      fileType === 'UNKNOWN' ||
      fileType === 'ORGANIZATION_PROFILE' ||
      fileType === 'LMA_OTHER' ||
      fileType === 'LMA_BATTERY_SIMULATION' ||
      fileType === 'LMA_GRID_USAGE_OPTIMIZATION' ||
      fileType === 'LMA_LOAD_SIMULATION' ||
      fileType === 'LMA_LOCATION_ANALYSIS'
    ) {
      filterUpdate.data['fileType'] = fileType;
    }

    if (mimeType) {
      filterUpdate.data['mimeType'] = mimeType;
    }

    if (filename) {
      filterUpdate.data['filename'] = filename;
    }

    if (
      orderBy === 'filename' ||
      orderBy === 'createdAt' ||
      orderBy === 'fileType' ||
      orderBy === 'sizeBytes'
    ) {
      filterUpdate.data['orderBy'] = orderBy;
    }
    if (orderDirection === 'asc' || orderDirection === 'desc') {
      filterUpdate.data['orderDirection'] = orderDirection;
    }

    this.documentsStore.filter.set(filterUpdate);
  }

  protected async onChangeFilter() {
    await new Promise((resolve) => setTimeout(resolve, 0)); // wait for form value to update
    const filterValue = this.documentsStore.filter();
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        fileType: filterValue.data.fileType || null,
        mimeType: filterValue.data.mimeType || null,
        filename: filterValue.data.filename || null,
        orderBy: filterValue.data.orderBy || null,
        orderDirection: filterValue.data.orderDirection || null,
      },
      queryParamsHandling: 'replace',
    });
  }

  protected onChangeOrder(event: {
    orderBy: DocumentsFilter['data']['orderBy'];
    orderDirection: DocumentsFilter['data']['orderDirection'];
  }) {
    this.documentsStore.filter.update((current) => ({
      ...current,
      data: {
        ...current.data,
        ...event,
      },
    }));
    this.onChangeFilter();
  }

  protected onLoadMore() {
    this.documentsStore.loadNextPage();
  }

  protected onUploadDialogSuccess(dialog: SimpleDialog) {
    dialog.close();
    this.documentsStore.reset();
    if (Object.values(this.documentsStore.pages()).length === 0) {
      this.documentsStore.loadPage(1);
    }
    this.notificationService.showSuccess({
      title: this.translocoService.translate('cockpit.documents.uploadDialog.successTitle'),
      subtitle: this.translocoService.translate('cockpit.documents.uploadDialog.successSubtitle'),
    });
  }

  protected onClickDeleteDocuments() {
    this.onDeleteDocuments(this.selectedRows());
  }

  protected async onDeleteDocuments(ids: string[]) {
    if (ids.length < 1) {
      throw new Error('Cannot delete an empty list of documents');
    }
    if (ids.length === 1) {
      return await this.onDeleteDocument(ids[0]);
    }
    const confirm = await this.confirmService.confirm({
      title: this.translocoService.translate('cockpit.documents.deleteConfirm.titlePlural', {
        count: ids.length,
      }),
      message: this.translocoService.translate('cockpit.documents.deleteConfirm.messagePlural'),
      cancelButtonText: this.translocoService.translate(
        'cockpit.documents.deleteConfirm.cancelButton',
      ),
      confirmButtonText: this.translocoService.translate(
        'cockpit.documents.deleteConfirm.confirmButton',
      ),
      mode: 'destructive',
    });
    if (!confirm) {
      return;
    }
    ids.forEach((id) => this.documentsStore.markForDeletion(id));
    this.table()?.selectedRows.set({});
    this.notificationService.show({
      component: DocumentsDeletedNotification,
      componentProps: {
        selectedIds: ids,
        clickResetCallback: () => ids.forEach((id) => this.documentsStore.unmarkForDeletion(id)),
      },
      duration: 3000,
      onDismissed: async (reason: string) => {
        if (reason !== 'timeout') {
          return;
        }
        try {
          await Promise.all(ids.map((id) => this.documentsStore.deleteDocument(id)));
        } catch (error) {
          if (error instanceof HttpErrorResponse && error.status === 409) {
            this.notificationService.showError({
              title: this.translocoService.translate('cockpit.documents.deleteError.title'),
              subtitle: this.translocoService.translate(
                'cockpit.documents.deleteError.conflictPlural',
              ),
            });
          } else {
            this.notificationService.showError({
              title: this.translocoService.translate('cockpit.documents.deleteError.title'),
              subtitle: this.translocoService.translate(
                'cockpit.documents.deleteError.subtitlePlural',
              ),
            });
          }
          this.documentsStore.unmarkAllForDeletion();
          throw error;
        }
      },
    });
  }

  private async onDeleteDocument(id: string) {
    const confirm = await this.confirmService.confirm({
      title: this.translocoService.translate('cockpit.documents.deleteConfirm.titleSingular'),
      message: this.translocoService.translate('cockpit.documents.deleteConfirm.messageSingular'),
      cancelButtonText: this.translocoService.translate(
        'cockpit.documents.deleteConfirm.cancelButton',
      ),
      confirmButtonText: this.translocoService.translate(
        'cockpit.documents.deleteConfirm.confirmButton',
      ),
      mode: 'destructive',
    });
    if (!confirm) {
      return;
    }
    this.documentsStore.markForDeletion(id);
    this.table()?.selectedRows.set({});
    this.notificationService.show({
      component: DocumentsDeletedNotification,
      componentProps: {
        selectedIds: [id],
        clickResetCallback: () => this.documentsStore.unmarkForDeletion(id),
      },
      duration: 3000,
      onDismissed: async (reason: string) => {
        if (reason !== 'timeout') {
          return;
        }
        try {
          await this.documentsStore.deleteDocument(id);
        } catch (error) {
          if (error instanceof HttpErrorResponse && error.status === 409) {
            this.notificationService.showError({
              title: this.translocoService.translate('cockpit.documents.deleteError.title'),
              subtitle: this.translocoService.translate(
                'cockpit.documents.deleteError.conflictSingular',
              ),
            });
          } else {
            this.notificationService.showError({
              title: this.translocoService.translate('cockpit.documents.deleteError.title'),
              subtitle: this.translocoService.translate(
                'cockpit.documents.deleteError.subtitleSingular',
              ),
            });
          }
          this.documentsStore.unmarkAllForDeletion();
          throw error;
        }
      },
    });
  }
}
